import React, { useEffect, useState, useRef } from 'react';
import CommonLayout from '../../../layouts/CommonLayout';
import { Container, Spinner, ProgressBar, Form, Modal } from 'react-bootstrap';
import { allAPiServicesCall } from '../../../services/apiServices';
import { getAuthConfig } from '../../../services/apiUtils';
import {
  FaStop,
  FaSync,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaRocket,
  FaLeaf,
  FaUpload,
  FaFileUpload,
  FaSearch,
  FaTrash,
  FaUsers,
  FaChevronLeft,
  FaChevronRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaPlay,
  FaEye,
  FaTimes,
  FaInfoCircle,
  FaMagic
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { extractObjectId } from '../../../utils/utils';
import './ContentPrewarming.css';

const UI_LOGS_STORAGE_KEY = 'content_prewarming_ui_logs_v1';
const UI_LOGS_MAX = 300;
const UI_LOGS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const ContentPrewarming = () => {
  const [loading, setLoading] = useState(false);
  const [batchJobs, setBatchJobs] = useState([]);
  const [batchJobsPagination, setBatchJobsPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [activeBatchJob, setActiveBatchJob] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [modalJobData, setModalJobData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalFocusSection, setModalFocusSection] = useState(null);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [uiLogs, setUiLogs] = useState([]);
  const batchPollingRef = useRef(null);
  const fileInputRef = useRef(null);
  const activeJobRef = useRef(null);
  const loggedBatchErrorsRef = useRef(new Set());
  const currentPageRef = useRef(1);
  const JOBS_PER_PAGE = 100;

  const appendUiLog = (entry) => {
    const next = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      timestamp: new Date().toISOString(),
      ...entry,
    };
    setUiLogs(prev => [next, ...prev].slice(0, UI_LOGS_MAX));
  };

  const clearUiLogs = () => {
    setUiLogs([]);
    try {
      localStorage.removeItem(UI_LOGS_STORAGE_KEY);
    } catch (error) {
      // no-op
    }
  };

  const isFailedResultItem = (item) => {
    if (!item) return false;
    if (typeof item.success === 'boolean') return item.success === false;
    if (typeof item.status === 'string') return item.status.toLowerCase() === 'failed';
    return !!item.error;
  };

  // CSV upload state
  const [csvGeniusIds, setCsvGeniusIds] = useState([]);
  const [csvFileName, setCsvFileName] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [previewPage, setPreviewPage] = useState(1);
  const previewLimit = 50;

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedArtists, setSelectedArtists] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    contentTypes: ['bio', 'reviews', 'comments', 'ratings', 'qa'],
    forceRegenerate: false,
  });

  // Bio regeneration state
  const [bioRegenLoading, setBioRegenLoading] = useState(false);
  const [bioRegenResult, setBioRegenResult] = useState(null);

  const contentTypeOptions = [
    { value: 'bio', label: 'Artist Bio' },
    { value: 'reviews', label: 'Reviews' },
    { value: 'comments', label: 'Comments' },
    { value: 'ratings', label: 'Ratings' },
    { value: 'qa', label: 'Q&A' },
    { value: 'meaning', label: 'Song Meanings' },
  ];

  useEffect(() => {
    try {
      const raw = localStorage.getItem(UI_LOGS_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const now = Date.now();
          const valid = parsed
            .filter(item => item && item.timestamp)
            .filter(item => {
              const ts = new Date(item.timestamp).getTime();
              return Number.isFinite(ts) && now - ts <= UI_LOGS_TTL_MS;
            })
            .slice(0, UI_LOGS_MAX);
          setUiLogs(valid);
        }
      }
    } catch (error) {
      // no-op
    }

    fetchBatchJobs();
    return () => {
      if (batchPollingRef.current) clearInterval(batchPollingRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(UI_LOGS_STORAGE_KEY, JSON.stringify(uiLogs.slice(0, UI_LOGS_MAX)));
    } catch (error) {
      // no-op
    }
  }, [uiLogs]);

  useEffect(() => {
    const pendingBatch = batchJobs.find(j => !j.isCompleted);
    const isProcessing = activeBatchJob?.isProcessingResults;
    const pollInterval = isProcessing ? 5000 : 30000;

    if (pendingBatch || isProcessing) {
      batchPollingRef.current = setInterval(() => {
        const jobId = isProcessing ? activeBatchJob.jobId : pendingBatch?.jobId;
        if (jobId) fetchBatchJobStatus(jobId);
      }, pollInterval);
    } else if (batchPollingRef.current) {
      clearInterval(batchPollingRef.current);
    }
    return () => {
      if (batchPollingRef.current) clearInterval(batchPollingRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchJobs, activeBatchJob?.isProcessingResults]);

  const fetchBatchJobs = async (page) => {
    const targetPage = page != null ? page : currentPageRef.current;
    try {
      const response = await allAPiServicesCall.getAllBatchJobs(null, getAuthConfig(), targetPage, JOBS_PER_PAGE);
      if (response?.data) {
        setBatchJobs(response.data);
        if (response.pagination) {
          setBatchJobsPagination(response.pagination);
          currentPageRef.current = response.pagination.page;
        }
        const unprocessedCompleted = response.data.find(
          (j) =>
            j.isCompleted &&
            j.totalRequests > 0 &&
            (j.completedRequests + j.failedRequests + (j.skippedRequests || 0)) === 0
        );
        const pending = response.data.find(j => !j.isCompleted);
        if (unprocessedCompleted && activeBatchJob?.jobId !== unprocessedCompleted.jobId) {
          fetchBatchJobStatus(unprocessedCompleted.jobId);
        } else if (pending) {
          fetchBatchJobStatus(pending.jobId);
        }
      }
    } catch (error) {
      appendUiLog({
        source: 'frontend',
        level: 'error',
        message: 'Failed to load batch jobs',
        details: error?.message || String(error),
      });
    }
  };

  const fetchBatchJobStatus = async (jobId, scrollToView = false) => {
    try {
      const response = await allAPiServicesCall.getBatchJobStatus(jobId, getAuthConfig());
      if (response?.data) {
        logBatchErrorsToConsole(response.data, 'status_poll');
        setActiveBatchJob(response.data);
        fetchBatchJobs();
        if (scrollToView) {
          setTimeout(() => {
            activeJobRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
        }
      }
    } catch (error) {
      appendUiLog({
        source: 'frontend',
        level: 'error',
        message: `Failed to fetch batch job status for ${jobId}`,
        details: error?.message || String(error),
      });
    }
  };

  const handleViewJobDetails = async (jobId, focusSection = null) => {
    setShowJobModal(true);
    setModalLoading(true);
    setModalJobData(null);
    setModalFocusSection(focusSection);
    try {
      const response = await allAPiServicesCall.getBatchJobStatus(jobId, getAuthConfig());
      if (response?.data) {
        logBatchErrorsToConsole(response.data, 'view_details');
        setModalJobData(response.data);
      }
    } catch (error) {
      appendUiLog({
        source: 'frontend',
        level: 'error',
        message: `Failed to load job details for ${jobId}`,
        details: error?.message || String(error),
      });
    }
    setModalLoading(false);
  };

  const logBatchErrorsToConsole = (jobData, source) => {
    if (!jobData) return;

    if (jobData.error) {
      const key = `${jobData.jobId || 'unknown'}::job_error::${jobData.error}`;
      if (!loggedBatchErrorsRef.current.has(key)) {
        console.error(`[Prewarm:${source}] Job error`, {
          jobId: jobData.jobId,
          status: jobData.status,
          contentTypes: jobData.contentTypes,
          error: jobData.error,
        });
        appendUiLog({
          source: 'backend',
          level: 'error',
          message: `Job error for ${jobData.jobId}`,
          details: {
            status: jobData.status,
            contentTypes: jobData.contentTypes,
            error: jobData.error,
          },
        });
        loggedBatchErrorsRef.current.add(key);
      }
    }

    if (!Array.isArray(jobData.recentResults)) return;

    jobData.recentResults
      .filter((item) => isFailedResultItem(item))
      .forEach((item, index) => {
        const key = `${jobData.jobId || 'unknown'}::${item.requestId || index}::${item.error || 'failed'}`;
        if (loggedBatchErrorsRef.current.has(key)) return;
        console.error(`[Prewarm:${source}] Failed item`, {
          jobId: jobData.jobId,
          requestId: item.requestId,
          contentType: item.contentType,
          entityId: item.entityId,
          error: item.error || 'Unknown failure',
        });
        appendUiLog({
          source: 'backend',
          level: 'error',
          message: `Failed item in ${jobData.jobId}`,
          details: {
            requestId: item.requestId,
            contentType: item.contentType,
            entityId: item.entityId,
            error: item.error || 'Unknown failure',
          },
        });
        loggedBatchErrorsRef.current.add(key);
      });
  };

  const handleCloseJobModal = () => {
    setShowJobModal(false);
    setModalJobData(null);
    setModalFocusSection(null);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').map(line => line.trim()).filter(line => line);
      const startIdx = lines[0].toLowerCase().includes('genius_id') ? 1 : 0;

      const ids = [];
      for (let i = startIdx; i < lines.length; i++) {
        const id = parseInt(lines[i]);
        if (!isNaN(id) && id > 0) {
          ids.push(id);
        }
      }

      if (ids.length === 0) {
        toast.error('No valid genius IDs found in CSV');
        return;
      }

      setCsvGeniusIds(ids);
      setCsvFileName(file.name);
      setPreviewPage(1);
      toast.success(`Loaded ${ids.length} genius IDs from ${file.name}`);
      handlePreviewCsv(ids, 1);
    };
    reader.readAsText(file);
  };

  const handlePreviewCsv = async (ids = csvGeniusIds, page = previewPage) => {
    if (ids.length === 0) {
      toast.error('Please upload a CSV file first');
      return;
    }

    setLoading(true);
    const response = await allAPiServicesCall.previewCsvArtists(ids, page, previewLimit, getAuthConfig());
    if (response?.data) {
      setPreviewData(response.data);
      setPreviewPage(page);
    }
    setLoading(false);
  };

  const handleSearch = async () => {
    if (searchTerm.length < 2) {
      toast.error('Search term must be at least 2 characters');
      return;
    }

    setLoading(true);
    const response = await allAPiServicesCall.searchArtistsForPrewarm(searchTerm, 100, getAuthConfig());
    if (response?.data) {
      setSearchResults(response.data);
    }
    setLoading(false);
  };

  const toggleArtistSelection = (artist) => {
    setSelectedArtists(prev => {
      const exists = prev.find(a => a.artist_id === artist.artist_id);
      if (exists) {
        return prev.filter(a => a.artist_id !== artist.artist_id);
      } else {
        return [...prev, artist];
      }
    });
  };

  const handleRegenerateBadBios = async (dryRun = false) => {
    setBioRegenLoading(true);
    setBioRegenResult(null);
    try {
      const params = { dryRun: String(dryRun) };
      const response = await allAPiServicesCall.regenerateBadBios(params, getAuthConfig());
      if (response?.data) {
        setBioRegenResult(response.data);
        if (!dryRun) {
          appendUiLog({ type: 'info', message: `Bio regen: ${response.message}` });
          fetchBatchJobs();
        }
      }
    } catch (err) {
      toast.error('Failed to regenerate bad bios');
      appendUiLog({ type: 'error', message: `Bio regen failed: ${err.message}` });
    } finally {
      setBioRegenLoading(false);
    }
  };

  const handleStartBatchJob = async () => {
    let idsToProcess = [];

    if (csvGeniusIds.length > 0) {
      idsToProcess = csvGeniusIds;
    } else if (selectedArtists.length > 0) {
      idsToProcess = selectedArtists.map(a => a.artist_id);
    }

    if (idsToProcess.length === 0) {
      toast.error('Please upload a CSV or select artists to process');
      return;
    }

    setLoading(true);
    try {
      const params = {
        contentTypes: formData.contentTypes.join(','),
        forceRegenerate: formData.forceRegenerate ? 'true' : 'false',
      };

      const response = await allAPiServicesCall.startBatchPrewarmJob(idsToProcess, params, getAuthConfig());
      if (response) {
        fetchBatchJobs();
        setCsvGeniusIds([]);
        setCsvFileName('');
        setPreviewData(null);
        setSelectedArtists([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBatchJob = async (jobId) => {
    await allAPiServicesCall.cancelBatchJob(jobId, getAuthConfig());
    setActiveBatchJob(null);
    fetchBatchJobs();
  };

  const handleDeleteBatchJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this batch job from history?')) {
      return;
    }
    await allAPiServicesCall.deleteBatchJob(jobId, getAuthConfig());
    if (activeBatchJob?.jobId === jobId) {
      setActiveBatchJob(null);
    }
    fetchBatchJobs();
  };

  const handleRerunBatchJob = async (jobId) => {
    const confirmed = window.confirm(
      'Start a new run for this batch job using the same artists and content types?',
    );
    if (!confirmed) return;

    const response = await allAPiServicesCall.rerunBatchJob(
      jobId,
      { forceRegenerate: 'false' },
      getAuthConfig(),
    );
    if (response) {
      fetchBatchJobs();
      handleCloseJobModal();
    }
  };

  const handleContentTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      contentTypes: prev.contentTypes.includes(type)
        ? prev.contentTypes.filter(t => t !== type)
        : [...prev.contentTypes, type]
    }));
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'JOB_STATE_PENDING': 'pending',
      'JOB_STATE_RUNNING': 'running',
      'JOB_STATE_SUCCEEDED': 'success',
      'JOB_STATE_FAILED': 'failed',
      'JOB_STATE_CANCELLED': 'cancelled',
      'JOB_STATE_EXPIRED': 'failed',
    };
    return statusMap[status] || 'pending';
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'JOB_STATE_PENDING': 'Pending',
      'JOB_STATE_RUNNING': 'Running',
      'JOB_STATE_SUCCEEDED': 'Completed',
      'JOB_STATE_FAILED': 'Failed',
      'JOB_STATE_CANCELLED': 'Cancelled',
      'JOB_STATE_EXPIRED': 'Expired',
    };
    return statusMap[status] || status;
  };

  const getSkippedRequests = (job) => {
    const explicit = Number(job?.skippedRequests || 0);
    if (explicit > 0) return explicit;

    const terminalStatuses = new Set([
      'JOB_STATE_SUCCEEDED',
      'JOB_STATE_FAILED',
      'JOB_STATE_CANCELLED',
      'JOB_STATE_EXPIRED',
    ]);
    const isTerminal =
      terminalStatuses.has(job?.status) || Boolean(job?.isCompleted);
    if (!isTerminal) return 0;

    const total = Number(job?.totalRequests || 0);
    const completed = Number(job?.completedRequests || 0);
    const failed = Number(job?.failedRequests || 0);
    const derived = total - completed - failed;
    return derived > 0 ? derived : 0;
  };

  const getProcessedRequests = (job) => {
    const processed = Number(job?.processedRequests || 0);
    if (processed > 0) return processed;
    return Number(job?.completedRequests || 0) + Number(job?.failedRequests || 0) + getSkippedRequests(job);
  };

  const getJobDisplayStatus = (job) => {
    if (!job) return '-';
    if (job.status === 'JOB_STATE_SUCCEEDED' && getProcessedRequests(job) < Number(job.totalRequests || 0)) {
      return 'Completed (Partial)';
    }
    return getStatusLabel(job.status);
  };

  const getSkippedItems = (jobData) => {
    if (!jobData) return [];
    if (Array.isArray(jobData.recentSkippedResults) && jobData.recentSkippedResults.length > 0) {
      return jobData.recentSkippedResults;
    }
    if (!Array.isArray(jobData.recentResults)) return [];
    return jobData.recentResults.filter(item => item?.status === 'skipped');
  };

  const hasPendingBatchJob = batchJobs.some(j => !j.isCompleted);
  const hasArtistsToProcess = csvGeniusIds.length > 0 || selectedArtists.length > 0;

  return (
    <CommonLayout>
      <div className="content-prewarming-page">
        <Container className="content-prewarming-container mt-5 pt-5">
          {/* Premium Header */}
          <div className="prewarming-header">
            <div className="prewarming-header-content">
              <div className="prewarming-header-left">
                <div className="prewarming-header-icon">
                  <FaRocket />
                </div>
                <div className="prewarming-header-text">
                  <h1>Content Pre-warming</h1>
                  <p>Generate AI content for artists in batch to improve page load times and SEO</p>
                </div>
              </div>
              <div className="prewarming-header-actions">
                <button className="prewarming-refresh-btn" onClick={fetchBatchJobs}>
                  <FaSync />
                  Refresh
                </button>
                <button className="prewarming-refresh-btn logs-btn" onClick={() => setShowLogsModal(true)}>
                  <FaInfoCircle />
                  Logs ({uiLogs.length})
                </button>
              </div>
            </div>
          </div>

          {/* Benefits Card */}
          <div className="benefits-card">
            <div className="benefits-card-title">
              <FaLeaf />
              Batch Mode Benefits
            </div>
            <ul className="benefits-list">
              <li><strong>50% cost savings</strong> compared to real-time API</li>
              <li>Results typically within a few hours (max 24h)</li>
              <li>Ideal for pre-planned bulk content generation</li>
              <li>Jobs persist even if you close the browser</li>
            </ul>
          </div>

          {/* Active Batch Job Progress */}
          {activeBatchJob && (
            <div ref={activeJobRef} className={`active-job-alert ${activeBatchJob.isCompleted ? (activeBatchJob.status === 'JOB_STATE_SUCCEEDED' ? 'success' : 'error') : 'pending'}`}>
              <div className="active-job-header">
                <div className="active-job-info">
                  {(!activeBatchJob.isCompleted || activeBatchJob.isProcessingResults) ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FaCheckCircle className="text-success" />
                  )}
                  <span className="active-job-title">{activeBatchJob.jobId}</span>
                  <span className={`status-badge ${getStatusBadgeClass(activeBatchJob.status)}`}>
                    {getJobDisplayStatus(activeBatchJob)}
                  </span>
                  {activeBatchJob.isProcessingResults && (
                    <span className="status-badge processing">Processing Results...</span>
                  )}
                </div>
                <div className="active-job-actions">
                  {!activeBatchJob.isCompleted && (
                    <button className="active-job-btn cancel" onClick={() => handleCancelBatchJob(activeBatchJob.jobId)}>
                      <FaStop /> Cancel
                    </button>
                  )}
                  <button className="active-job-btn close" onClick={() => setActiveBatchJob(null)}>
                    <FaTimesCircle /> Close
                  </button>
                </div>
              </div>

              {activeBatchJob.isProcessingResults && (
                <div className="active-job-progress">
                  <ProgressBar
                    animated
                    striped
                    now={activeBatchJob.percentComplete || 0}
                  />
                  <div className="active-job-progress-label">
                    Saving results: {getProcessedRequests(activeBatchJob)}/{activeBatchJob.totalRequests} ({activeBatchJob.percentComplete || 0}%)
                  </div>
                </div>
              )}

              <div className="active-job-stats">
                <span>Total Requests: <strong>{activeBatchJob.totalRequests}</strong></span>
                <span>Completed: <strong className="text-success">{activeBatchJob.completedRequests}</strong></span>
                <span>Failed: <strong className="text-danger">{activeBatchJob.failedRequests}</strong></span>
                <span>Skipped: <strong className="text-warning">{getSkippedRequests(activeBatchJob)}</strong></span>
              </div>

              <div className="active-job-meta">
                {activeBatchJob.startedAt && <span>Started: {new Date(activeBatchJob.startedAt).toLocaleString()}</span>}
                {activeBatchJob.completedAt && <span> • Completed: {new Date(activeBatchJob.completedAt).toLocaleString()}</span>}
                {activeBatchJob.contentTypes && <span> • Content: {activeBatchJob.contentTypes.join(', ')}</span>}
              </div>

              {activeBatchJob.error && (
                <div className="active-job-error">
                  <strong>Error:</strong> {activeBatchJob.error}
                </div>
              )}
            </div>
          )}

          {/* Action Cards */}
          <div className="action-cards-grid">
            {/* CSV Upload Card */}
            <div className="action-card">
              <div className="action-card-header upload">
                <FaUpload />
                Upload Artist CSV
              </div>
              <div className="action-card-body">
                <div className="prewarming-form-group">
                  <label className="prewarming-form-label">Upload CSV with Genius IDs</label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    ref={fileInputRef}
                    className="prewarming-file-input"
                  />
                  <p className="prewarming-form-hint">CSV format: One genius_id per line (header optional)</p>
                </div>

                {csvFileName && (
                  <div className="csv-loaded-alert">
                    <FaFileUpload />
                    <span><strong>{csvFileName}</strong> - {csvGeniusIds.length} IDs loaded</span>
                  </div>
                )}

                <div className="prewarming-divider"></div>

                <div className="prewarming-form-group">
                  <label className="prewarming-form-label">Or Search Artists by Name</label>
                  <div className="prewarming-search-group">
                    <input
                      type="text"
                      placeholder="Search artist name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      className="prewarming-text-input"
                    />
                    <button className="prewarming-search-btn" onClick={handleSearch} disabled={loading}>
                      <FaSearch />
                    </button>
                  </div>
                </div>

                {selectedArtists.length > 0 && (
                  <div className="selected-artists-alert">
                    <span><strong>{selectedArtists.length}</strong> artists selected from search</span>
                    <button className="clear-btn" onClick={() => setSelectedArtists([])}>Clear</button>
                  </div>
                )}
              </div>
            </div>

            {/* Configuration Card */}
            <div className="action-card">
              <div className="action-card-header batch">
                <FaPlay />
                Start Batch Job (50% Cheaper)
              </div>
              <div className="action-card-body">
                <div className="prewarming-form-group">
                  <label className="prewarming-form-label">Content Types to Generate</label>
                  <div className="content-types-grid">
                    {contentTypeOptions.map(option => (
                      <label
                        key={option.value}
                        className={`content-type-checkbox ${formData.contentTypes.includes(option.value) ? 'checked' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.contentTypes.includes(option.value)}
                          onChange={() => handleContentTypeChange(option.value)}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="force-regenerate-group">
                  <Form.Check
                    type="switch"
                    id="force-regenerate"
                    checked={formData.forceRegenerate}
                    onChange={(e) => setFormData(prev => ({ ...prev, forceRegenerate: e.target.checked }))}
                  />
                  <div className="force-regenerate-text">
                    <label htmlFor="force-regenerate">Force Regenerate</label>
                    <small>Warning: This will overwrite existing content</small>
                  </div>
                </div>

                <button
                  className="start-batch-btn"
                  onClick={handleStartBatchJob}
                  disabled={loading || hasPendingBatchJob || formData.contentTypes.length === 0 || !hasArtistsToProcess}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" />
                      Starting...
                    </>
                  ) : (
                    <>
                      <FaLeaf />
                      Start Batch Job ({csvGeniusIds.length || selectedArtists.length} artists)
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Regenerate Bad Bios Section */}
          <div className="bio-regen-card">
            <div className="bio-regen-header">
              <div className="bio-regen-header-left">
                <FaMagic />
                <div>
                  <h3>Regenerate Bad Bios</h3>
                  <p>Find artists with short (&lt;100 words), missing, or placeholder bios and batch regenerate them</p>
                </div>
              </div>
              <div className="bio-regen-actions">
                <button
                  className="bio-regen-btn preview"
                  onClick={() => handleRegenerateBadBios(true)}
                  disabled={bioRegenLoading}
                >
                  {bioRegenLoading ? <Spinner animation="border" size="sm" /> : <FaEye />}
                  Dry Run
                </button>
                <button
                  className="bio-regen-btn submit"
                  onClick={() => handleRegenerateBadBios(false)}
                  disabled={bioRegenLoading}
                >
                  {bioRegenLoading ? <Spinner animation="border" size="sm" /> : <FaRocket />}
                  Submit Batch
                </button>
              </div>
            </div>

            {bioRegenResult && (
              <div className="bio-regen-result">
                <div className="bio-regen-stats">
                  <div className="bio-regen-stat">
                    <span className="bio-regen-stat-value">{bioRegenResult.totalFound?.toLocaleString() || 0}</span>
                    <span className="bio-regen-stat-label">Total Found</span>
                  </div>
                  <div className="bio-regen-stat success">
                    <span className="bio-regen-stat-value">{bioRegenResult.totalEligible?.toLocaleString() || 0}</span>
                    <span className="bio-regen-stat-label">Eligible</span>
                  </div>
                  <div className="bio-regen-stat warning">
                    <span className="bio-regen-stat-value">{bioRegenResult.totalExcludedByActiveBatch?.toLocaleString() || 0}</span>
                    <span className="bio-regen-stat-label">Already In Batch</span>
                  </div>
                  <div className="bio-regen-stat info">
                    <span className="bio-regen-stat-value">{bioRegenResult.totalSubmitted?.toLocaleString() || 0}</span>
                    <span className="bio-regen-stat-label">Submitted</span>
                  </div>
                </div>

                {bioRegenResult.breakdown && (
                  <div className="bio-regen-breakdown">
                    <span>Missing/Placeholder: <strong>{bioRegenResult.breakdown.missingOrPlaceholder?.toLocaleString()}</strong></span>
                    <span>Short Bio (&lt;100 words): <strong>{bioRegenResult.breakdown.shortBio?.toLocaleString()}</strong></span>
                  </div>
                )}

                {bioRegenResult.dryRun && bioRegenResult.sample && bioRegenResult.sample.length > 0 && (
                  <div className="bio-regen-sample">
                    <div className="bio-regen-sample-title">Sample Artists (first 20)</div>
                    <div className="bio-regen-sample-list">
                      {bioRegenResult.sample.map((a, i) => (
                        <div key={i} className="bio-regen-sample-item">
                          <strong>{a.artist_name}</strong>
                          <span className="mono">ID: {a.artist_id}</span>
                          {a.wordCount && <span className="bio-regen-word-count">{a.wordCount} words</span>}
                          {!a.ai_bio_generated && <span className="bio-regen-tag missing">No AI bio</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {bioRegenResult.jobIds && bioRegenResult.jobIds.length > 0 && (
                  <div className="bio-regen-jobs">
                    <strong>Job IDs:</strong> {bioRegenResult.jobIds.join(', ')}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="data-card">
              <div className="data-card-header">
                <div className="data-card-title">
                  <FaSearch />
                  Search Results
                  <span className="data-card-badge success">{searchResults.length}</span>
                </div>
                <div className="data-card-actions">
                  <button className="data-card-btn" onClick={() => setSearchResults([])}>Hide</button>
                </div>
              </div>
              <div className="data-card-body">
                <table className="prewarming-table">
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}>Select</th>
                      <th>Artist Name</th>
                      <th>Genius ID</th>
                      <th>Spotify Listeners</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map((artist) => (
                      <tr key={extractObjectId(artist._id)}>
                        <td>
                          <input
                            type="checkbox"
                            className="table-checkbox"
                            checked={selectedArtists.some(a => a.artist_id === artist.artist_id)}
                            onChange={() => toggleArtistSelection(artist)}
                          />
                        </td>
                        <td>{artist.artist_name}</td>
                        <td className="mono">{artist.artist_id}</td>
                        <td>{artist.spotifyListeners?.toLocaleString() || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* CSV Preview */}
          {previewData && (
            <div className="data-card">
              <div className="data-card-header">
                <div className="data-card-title">
                  <FaUsers />
                  CSV Preview - Page {previewData.pagination.page} of {previewData.pagination.totalPages}
                  <span className="data-card-badge success">{previewData.pagination.totalFound ?? previewData.pagination.foundOnPage} found</span>
                  {(previewData.pagination.totalNotFound > 0 || previewData.pagination.notFoundOnPage > 0) && (
                    <span className="data-card-badge warning">{previewData.pagination.totalNotFound ?? previewData.pagination.notFoundOnPage} not found</span>
                  )}
                </div>
                <div className="data-card-actions">
                  <button className="data-card-btn" onClick={() => setPreviewData(null)}>Hide Preview</button>
                </div>
              </div>

              {previewData.notFound.length > 0 && (
                <div className="not-found-alert">
                  <strong>IDs not found in database:</strong> {previewData.notFound.join(', ')}
                </div>
              )}

              <div className="data-card-body">
                <table className="prewarming-table">
                  <thead>
                    <tr>
                      <th style={{ width: '60px' }}>#</th>
                      <th>Genius ID</th>
                      <th>Artist Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.artists.map((artist, idx) => (
                      <tr key={extractObjectId(artist._id)}>
                        <td>{(previewPage - 1) * previewLimit + idx + 1}</td>
                        <td className="mono">{artist.artist_id}</td>
                        <td>{artist.artist_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="prewarming-pagination">
                <span className="prewarming-pagination-info">
                  Total: {previewData.pagination.total} genius IDs in CSV
                </span>
                <div className="prewarming-pagination-buttons">
                  <button
                    className="prewarming-pagination-btn"
                    onClick={() => handlePreviewCsv(csvGeniusIds, 1)}
                    disabled={previewPage === 1}
                  >
                    <FaAngleDoubleLeft />
                  </button>
                  <button
                    className="prewarming-pagination-btn"
                    onClick={() => handlePreviewCsv(csvGeniusIds, previewPage - 1)}
                    disabled={previewPage === 1}
                  >
                    <FaChevronLeft />
                  </button>
                  <button className="prewarming-pagination-btn active">{previewPage}</button>
                  <button
                    className="prewarming-pagination-btn"
                    onClick={() => handlePreviewCsv(csvGeniusIds, previewPage + 1)}
                    disabled={previewPage >= previewData.pagination.totalPages}
                  >
                    <FaChevronRight />
                  </button>
                  <button
                    className="prewarming-pagination-btn"
                    onClick={() => handlePreviewCsv(csvGeniusIds, previewData.pagination.totalPages)}
                    disabled={previewPage >= previewData.pagination.totalPages}
                  >
                    <FaAngleDoubleRight />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Batch Jobs History */}
          {batchJobs.length > 0 && (
            <div className="data-card">
              <div className="data-card-header">
                <div className="data-card-title">
                  <FaClock />
                  Batch Jobs History
                  <span className="data-card-badge success">{batchJobsPagination.total}</span>
                </div>
              </div>
              <div className="data-card-body">
                <table className="prewarming-table">
                  <thead>
                    <tr>
                      <th>Job ID</th>
                      <th>Status</th>
                      <th>Requests</th>
                      <th>Completed</th>
                      <th>Failed</th>
                      <th>Skipped</th>
                      <th>Content Types</th>
                      <th>Started</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchJobs.map(job => (
                      <tr key={job.jobId}>
                        <td className="mono" style={{ fontSize: '12px' }}>{job.jobId.replace('batch_', '').slice(0, 15)}...</td>
                        <td>
                          <span className={`status-badge ${getStatusBadgeClass(job.status)}`}>
                            {getJobDisplayStatus(job)}
                          </span>
                        </td>
                        <td>{job.totalRequests}</td>
                        <td className="text-success">{job.completedRequests}</td>
                        <td className="text-danger">{job.failedRequests}</td>
                        <td className="text-warning">
                          {getSkippedRequests(job) > 0 ? (
                            <button
                              type="button"
                              className="history-btn view"
                              onClick={() => handleViewJobDetails(job.jobId, 'skipped')}
                              title="View skipped request reasons"
                            >
                              {getSkippedRequests(job)}
                            </button>
                          ) : (
                            0
                          )}
                        </td>
                        <td style={{ fontSize: '12px' }}>{job.contentTypes?.join(', ') || '-'}</td>
                        <td style={{ fontSize: '12px' }}>{job.startedAt ? new Date(job.startedAt).toLocaleString() : 'Invalid Date'}</td>
                        <td>
                          <div className="history-actions">
                            {!job.isCompleted ? (
                              <button className="history-btn cancel" onClick={() => handleCancelBatchJob(job.jobId)}>
                                Cancel
                              </button>
                            ) : (
                              <button className="history-btn view" onClick={() => handleViewJobDetails(job.jobId)}>
                                <FaEye /> View
                              </button>
                            )}
                            <button
                              className="history-btn delete"
                              onClick={() => handleDeleteBatchJob(job.jobId)}
                              title="Delete from history"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {batchJobsPagination.totalPages > 1 && (
                <div className="prewarming-pagination">
                  <span className="prewarming-pagination-info">
                    Page {batchJobsPagination.page} of {batchJobsPagination.totalPages} ({batchJobsPagination.total} jobs)
                  </span>
                  <div className="prewarming-pagination-buttons">
                    <button
                      className="prewarming-pagination-btn"
                      onClick={() => fetchBatchJobs(1)}
                      disabled={batchJobsPagination.page === 1}
                    >
                      <FaAngleDoubleLeft />
                    </button>
                    <button
                      className="prewarming-pagination-btn"
                      onClick={() => fetchBatchJobs(batchJobsPagination.page - 1)}
                      disabled={batchJobsPagination.page === 1}
                    >
                      <FaChevronLeft />
                    </button>
                    <button className="prewarming-pagination-btn active">{batchJobsPagination.page}</button>
                    <button
                      className="prewarming-pagination-btn"
                      onClick={() => fetchBatchJobs(batchJobsPagination.page + 1)}
                      disabled={batchJobsPagination.page >= batchJobsPagination.totalPages}
                    >
                      <FaChevronRight />
                    </button>
                    <button
                      className="prewarming-pagination-btn"
                      onClick={() => fetchBatchJobs(batchJobsPagination.totalPages)}
                      disabled={batchJobsPagination.page >= batchJobsPagination.totalPages}
                    >
                      <FaAngleDoubleRight />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Job Details Modal */}
          <Modal
            show={showJobModal}
            onHide={handleCloseJobModal}
            centered
            size="lg"
            className="job-details-modal"
          >
            <Modal.Body className="job-details-modal-body">
              <button className="job-modal-close" onClick={handleCloseJobModal}>
                <FaTimes />
              </button>

              {modalLoading ? (
                <div className="job-modal-loading">
                  <Spinner animation="border" />
                  <span>Loading job details...</span>
                </div>
              ) : modalJobData && (
                <>
                  <div className="job-modal-header">
                    <div className={`job-modal-icon ${getStatusBadgeClass(modalJobData.status)}`}>
                      {modalJobData.status === 'JOB_STATE_SUCCEEDED' ? <FaCheckCircle /> :
                       modalJobData.status === 'JOB_STATE_FAILED' ? <FaTimesCircle /> :
                       modalJobData.status === 'JOB_STATE_CANCELLED' ? <FaTimesCircle /> :
                       <FaInfoCircle />}
                    </div>
                    <div className="job-modal-title-section">
                      <h3 className="job-modal-title">Batch Job Details</h3>
                      <span className={`status-badge ${getStatusBadgeClass(modalJobData.status)}`}>
                        {getJobDisplayStatus(modalJobData)}
                      </span>
                    </div>
                  </div>

                  <div className="job-modal-id">
                    <span className="job-modal-id-label">Job ID:</span>
                    <code className="job-modal-id-value">{modalJobData.jobId}</code>
                  </div>

                  <div className="job-modal-stats-grid">
                    <div className="job-modal-stat">
                      <span className="job-modal-stat-value">{modalJobData.totalRequests}</span>
                      <span className="job-modal-stat-label">Total Requests</span>
                    </div>
                    <div className="job-modal-stat success">
                      <span className="job-modal-stat-value">{modalJobData.completedRequests}</span>
                      <span className="job-modal-stat-label">Completed</span>
                    </div>
                    <div className="job-modal-stat danger">
                      <span className="job-modal-stat-value">{modalJobData.failedRequests}</span>
                      <span className="job-modal-stat-label">Failed</span>
                    </div>
                    <div className="job-modal-stat warning">
                      <span className="job-modal-stat-value">{getSkippedRequests(modalJobData)}</span>
                      <span className="job-modal-stat-label">Skipped</span>
                    </div>
                    <div className="job-modal-stat info">
                      <span className="job-modal-stat-value">
                        {(() => {
                          const processed = getProcessedRequests(modalJobData);
                          if (processed <= 0) return '0%';
                          return `${Math.round((modalJobData.completedRequests / processed) * 100)}%`;
                        })()}
                      </span>
                      <span className="job-modal-stat-label">Success Rate</span>
                    </div>
                  </div>

                  {modalJobData.isProcessingResults && (
                    <div className="job-modal-progress">
                      <div className="job-modal-progress-label">
                        <Spinner animation="border" size="sm" />
                        Processing Results: {modalJobData.percentComplete || 0}%
                      </div>
                      <ProgressBar animated striped now={modalJobData.percentComplete || 0} />
                    </div>
                  )}

                  <div className="job-modal-details">
                    <div className="job-modal-detail-row">
                      <span className="job-modal-detail-label">Content Types</span>
                      <span className="job-modal-detail-value">
                        {modalJobData.contentTypes?.map(type => (
                          <span key={type} className="content-type-tag">{type}</span>
                        )) || '-'}
                      </span>
                    </div>
                    <div className="job-modal-detail-row">
                      <span className="job-modal-detail-label">Started At</span>
                      <span className="job-modal-detail-value">
                        {modalJobData.startedAt ? new Date(modalJobData.startedAt).toLocaleString() : '-'}
                      </span>
                    </div>
                    {modalJobData.completedAt && (
                      <div className="job-modal-detail-row">
                        <span className="job-modal-detail-label">Completed At</span>
                        <span className="job-modal-detail-value">
                          {new Date(modalJobData.completedAt).toLocaleString()}
                        </span>
                      </div>
                    )}
                    {modalJobData.startedAt && modalJobData.completedAt && (
                      <div className="job-modal-detail-row">
                        <span className="job-modal-detail-label">Duration</span>
                        <span className="job-modal-detail-value">
                          {(() => {
                            const duration = new Date(modalJobData.completedAt) - new Date(modalJobData.startedAt);
                            const hours = Math.floor(duration / 3600000);
                            const minutes = Math.floor((duration % 3600000) / 60000);
                            const seconds = Math.floor((duration % 60000) / 1000);
                            return `${hours > 0 ? hours + 'h ' : ''}${minutes}m ${seconds}s`;
                          })()}
                        </span>
                      </div>
                    )}
                  </div>

                  {modalJobData.error && (
                    <div className="job-modal-error">
                      <strong>Error:</strong> {modalJobData.error}
                    </div>
                  )}

                  {Array.isArray(modalJobData.recentResults) &&
                    modalJobData.recentResults.some(item => isFailedResultItem(item)) && (
                    <div className="job-modal-error">
                      <strong>Recent Failed Items:</strong>
                      <div className="job-modal-list">
                        {modalJobData.recentResults
                          .filter(item => isFailedResultItem(item))
                          .slice(0, 20)
                          .map((item, idx) => (
                            <div key={`${item.requestId || 'failed'}-${idx}`} className="job-modal-list-item">
                              <code>{item.requestId || 'unknown-request'}</code>
                              {' - '}
                              {item.error || 'Unknown failure'}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {getSkippedItems(modalJobData).length > 0 && (
                    <div className={`job-modal-skipped ${modalFocusSection === 'skipped' ? 'focus' : ''}`}>
                      <div className="job-modal-skipped-title">Skipped Items ({getSkippedRequests(modalJobData)})</div>
                      <div className="job-modal-list">
                        {getSkippedItems(modalJobData)
                          .slice(0, 30)
                          .map((item, idx) => (
                            <div key={`${item.requestId || 'skipped'}-${idx}`} className="job-modal-list-item">
                              <span>
                                {item.songName
                                  ? `${item.artistName || ''} - ${item.songName}`
                                  : item.artistName || (<code>{item.requestId || 'unknown-request'}</code>)}
                              </span>
                              {' - '}
                              {item.error || 'Skipped (no detailed reason)'}
                            </div>
                          ))}
                        {getSkippedItems(modalJobData).length > 30 && (
                          <div className="job-modal-list-item" style={{ color: '#999' }}>
                            ...and {getSkippedItems(modalJobData).length - 30} more
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {modalFocusSection === 'skipped' && getSkippedItems(modalJobData).length === 0 && (
                    <div className="job-modal-skipped focus">
                      <strong>Vertex AI did not return results for {getSkippedRequests(modalJobData)} requests. This typically happens when the AI provider drops some requests during batch processing.</strong>
                    </div>
                  )}

                  <div className="job-modal-actions">
                    <button className="job-modal-btn secondary" onClick={handleCloseJobModal}>
                      Close
                    </button>
                    {modalJobData.isCompleted && (
                      <button
                        className="job-modal-btn primary"
                        onClick={() => handleRerunBatchJob(modalJobData.jobId)}
                      >
                        <FaSync /> Re-run Job
                      </button>
                    )}
                    {!modalJobData.isCompleted && (
                      <button
                        className="job-modal-btn danger"
                        onClick={() => {
                          handleCancelBatchJob(modalJobData.jobId);
                          handleCloseJobModal();
                        }}
                      >
                        <FaStop /> Cancel Job
                      </button>
                    )}
                  </div>
                </>
              )}
            </Modal.Body>
          </Modal>

          <Modal
            show={showLogsModal}
            onHide={() => setShowLogsModal(false)}
            centered
            size="lg"
            className="job-details-modal logs-modal"
          >
            <Modal.Body className="job-details-modal-body logs-modal-body">
              <button className="job-modal-close" onClick={() => setShowLogsModal(false)}>
                <FaTimes />
              </button>
              <div className="logs-modal-header">
                <div className="logs-modal-title-wrap">
                  <div className="logs-modal-icon">
                    <FaInfoCircle />
                  </div>
                  <div className="job-modal-title-section">
                    <h3 className="job-modal-title">Frontend + Backend Logs</h3>
                    <p className="logs-modal-subtitle">Live error traces from API calls and batch processing</p>
                  </div>
                </div>
              </div>

              <div className="logs-modal-toolbar">
                <span className="logs-count-badge">{uiLogs.length} entries</span>
                <button className="job-modal-btn secondary" onClick={clearUiLogs}>
                  Clear Logs
                </button>
              </div>

              <div className="logs-feed">
                {uiLogs.length === 0 ? (
                  <div className="logs-empty">
                    <FaInfoCircle />
                    <span>No logs yet.</span>
                  </div>
                ) : (
                  uiLogs.map((log) => (
                    <div key={log.id} className={`logs-entry ${log.level === 'error' ? 'error' : 'info'}`}>
                      <strong>[{new Date(log.timestamp).toLocaleTimeString()}] [{log.source}] {log.level?.toUpperCase()}</strong>
                      <div>{log.message}</div>
                      {log.details && (
                        <pre className="logs-entry-pre">
                          {typeof log.details === 'string' ? log.details : JSON.stringify(log.details, null, 2)}
                        </pre>
                      )}
                    </div>
                  ))
                )}
              </div>
            </Modal.Body>
          </Modal>
        </Container>
      </div>
    </CommonLayout>
  );
};

export default ContentPrewarming;
